import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import {
  endpoints,
  IProduct,
  IProductVariant,
  IWishlist,
  isVariantInStock,
  priceFormat,
} from "muminsmart-utils";
import { cn } from "@/lib/utils";
import { inter, philosopher } from "@/lib/fonts";
import { routes } from "@/lib/constants";
import {
  apiSlice,
  useAddCartItemMutation,
  useAddWishlistItemMutation,
  useRemoveWishlistItemMutation,
} from "@/lib/api/apiSlice";
import { useAppDispatch } from "@/lib/hooks";
import { useCartWishlist } from "@/lib/hooks/useCartWishlist";
import { usePrefetchProduct } from "@/lib/hooks/usePrefetchProduct";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/contexts/AuthContext";
import { toast } from "sonner";
import CustomImage from "../common/Image";
import { Button } from "@/components/ui/button";

interface ProductCardProps {
  variant: IProductVariant;
  product?: Pick<IProduct, "id" | "name" | "description">;
}

function ProductCard({ product, variant }: ProductCardProps) {
  const productData = product || variant.product;
  const productId = productData?.id ?? "";
  const prefetchProduct = usePrefetchProduct();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { status } = useSession();
  const { isInCart, isWishlisted, getWishlistId } = useCartWishlist();
  const [addCartItem] = useAddCartItemMutation();
  const [addWishlistItem] = useAddWishlistItemMutation();
  const [removeWishlistItem] = useRemoveWishlistItemMutation();
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);

  const variantInCart = isInCart(variant.id);
  const variantWishlisted = isWishlisted(variant.id);
  const wishlistId = getWishlistId(variant.id);

  const handleWishlistToggle = async () => {
    if (isWishlistLoading) {
      return;
    }

    if (status !== "authenticated") {
      toast.error("Please login to add items to wishlist");
      navigate(routes.auth.login);
      return;
    }

    setIsWishlistLoading(true);

    try {
      if (variantWishlisted) {
        let currentWishlistId = wishlistId || null;

        if (!currentWishlistId) {
          const response = await dispatch(
            apiSlice.endpoints.fetchAll.initiate(
              {
                endpoint: endpoints.wishlist.index,
                params: { productVariantId: variant.id },
              },
              { subscribe: false },
            ),
          ).unwrap();

          currentWishlistId = (response as IWishlist[])?.[0]?.id || null;
        }

        if (!currentWishlistId) {
          return;
        }

        await removeWishlistItem(currentWishlistId).unwrap();
        return;
      }

      await addWishlistItem({
        productId,
        productVariantId: variant.id,
      }).unwrap();
    } catch {
      toast.error(
        variantWishlisted
          ? "Failed to remove from wishlist"
          : "Failed to add to wishlist",
      );
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (isCartLoading) {
      return;
    }

    if (variantInCart) {
      navigate(routes.cart);
      return;
    }

    if (status !== "authenticated") {
      toast.error("Please login to add items to cart");
      navigate(routes.auth.login);
      return;
    }

    setIsCartLoading(true);

    try {
      await addCartItem({
        productVariantId: variant.id,
        quantity: 1,
      }).unwrap();
    } catch {
      toast.error("Failed to add item to cart");
    } finally {
      setIsCartLoading(false);
    }
  };

  const isOutOfStock = !isVariantInStock(variant);

  const discountPct =
    variant.price > 0 && variant.price !== variant.discountedPrice
      ? Math.round(
          ((variant.price - variant.discountedPrice) / variant.price) * 100,
        )
      : 0;

  return (
    <div
      className={cn(
        "flex flex-col justify-between bg-[#FDFAF7] rounded-t-md p-1",
        inter.className,
      )}
    >
      <div>
        <div className="relative rounded-md overflow-hidden bg-[#DAEEF0] aspect-3/4">
          <Link
            to={routes.products.id(productId, variant.id)}
            className="h-full w-full"
            onMouseEnter={() => prefetchProduct(productId)}
            onFocus={() => prefetchProduct(productId)}
          >
            <CustomImage
              src={variant.featuredImage}
              alt={productData?.name || variant.sku}
              className="h-full w-full object-cover"
            />
          </Link>

          <button
            type="button"
            aria-label={
              variantWishlisted ? "Remove from wishlist" : "Add to wishlist"
            }
            className={cn(
              "absolute right-2.5 top-2.5 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md",
              isWishlistLoading && "pointer-events-none opacity-60",
            )}
            onClick={() => {
              void handleWishlistToggle();
            }}
          >
            <Heart
              strokeWidth={1.5}
              className={cn(
                "h-4 w-4 transition-colors",
                variantWishlisted
                  ? "fill-primary text-primary"
                  : "text-gray-400",
              )}
            />
          </button>

          {discountPct > 0 && (
            <span
              className={
                "absolute bottom-2.5 left-2.5 rounded-full bg-secondary px-2.5 py-1 text-[10px] font-bold leading-none text-white"
              }
            >
              {discountPct}% OFF
            </span>
          )}
          {isOutOfStock && (
            <span className="absolute inset-x-0 top-1/2 z-10 flex w-full -translate-y-1/2 items-center justify-center bg-black/40 py-0.5 text-center text-xs font-bold text-white">
              Out Of Stock
            </span>
          )}
        </div>

        <Link
          to={routes.products.id(productId)}
          className="pl-2 mt-2 flex flex-col gap-0.5 px-0.5"
        >
          <p
            className={cn(
              "text-md font-semibold line-clamp-1",
              philosopher.className,
            )}
          >
            {productData?.name || variant.sku}
          </p>
          <p className="mt-0.5 line-clamp-1 text-xs text-[#6f6f6f]">
            {productData?.description || ""}
          </p>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span
              className="text-xs text-[#6f6f6f] line-clamp-1"
              title={variant.variantAttributes
                .map((attr) => attr.value)
                .join(" | ")}
            >
              {variant.variantAttributes.map((attr) => attr.value).join(" | ")}
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-md font-bold text-[#1a1a1a]">
              {priceFormat(variant.discountedPrice)}
            </span>
            {variant.price !== variant.discountedPrice && (
              <span className="text-sm text-[#9e9e9e] line-through">
                {priceFormat(variant.price)}
              </span>
            )}
          </div>
        </Link>
      </div>
      <div className="my-2 flex items-center justify-center">
        {isOutOfStock ? (
          <div className="w-[90%] flex flex-col items-center gap-2">
            {variantWishlisted ? (
              <Button
                asChild
                size="sm"
                className="w-full rounded-full bg-secondary text-white shadow-md p-1"
              >
                <Link to={routes.wishlist} aria-label="View wishlist">
                  View Wishlist
                </Link>
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label="Add to wishlist"
                className={cn(
                  "w-full rounded-full shadow-md p-1",
                  isWishlistLoading && "pointer-events-none opacity-60",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleWishlistToggle();
                }}
              >
                Add To Wishlist
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            aria-label={variantInCart ? "View cart" : "Add to cart"}
            className={cn(
              "w-[90%] rounded-full shadow-md p-1",
              isCartLoading && "pointer-events-none opacity-60",
              variantInCart ? "bg-secondary text-white" : "",
            )}
            onClick={(e) => {
              e.stopPropagation();
              void handleAddToCart();
            }}
          >
            {variantInCart ? "View cart" : "Add To Cart"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default React.memo(ProductCard);
